import {
  Autocomplete,
  AutocompleteItem,
  AutocompleteSection,
} from "@heroui/react";
import { FieldValues, Path, PathValue } from "react-hook-form";
import { SectionAutocompleteFieldProps } from "./types";
import { useQuery } from "@tanstack/react-query";
import { getAuthorized } from "@/lib/fetch";
import { useState, useEffect } from "react";

interface TranslationRecord {
  [key: string]: string;
}

interface TranslationResponse {
  [section: string]: TranslationRecord;
}

export function SectionAutocompleteField<T extends FieldValues>({
  field,
  setValue,
  watch,
}: {
  field: SectionAutocompleteFieldProps;
  setValue: (name: Path<T>, value: PathValue<T, Path<T>>) => void;
  watch: (name: Path<T>) => any;
}) {
  const selectedValue = watch(field.name as Path<T>) || "";
  const [options, setOptions] = useState<
    Array<{
      section: string;
      items: Array<{ value: string; label: string }>;
    }>
  >([]);
  const [selectedLabel, setSelectedLabel] = useState("");

  // Fetch translations if useTranslation is true
  const { data: translations, isLoading } = useQuery<TranslationResponse>({
    queryKey: [field.table_name || "", "translations", field.language || "fr"],
    queryFn: async () => {
      if (!field.useTranslation || !field.client || !field.table_name) {
        return {};
      }

      const res = await getAuthorized(
        `${import.meta.env.VITE_BACKEND_URL}/${field.client}/${
          field.table_name
        }/translations?language=${field.language || "fr"}`,
      );

      if (!res.ok) {
        throw new Error("Failed to fetch translations");
      }

      return res.json();
    },
    enabled: field.useTranslation === true,
  });

  // Process options based on translations
  useEffect(() => {
    if (field.useTranslation && translations) {
      const section_key = field.translate_key || "";
      const translationSection = translations[section_key] || {};

      // Transform options using translations
      const transformedOptions = field.options.map((section) => ({
        section: section.section_name,
        items: (section.fields || []).map((fieldKey) => ({
          value: fieldKey,
          label: translationSection[fieldKey] || fieldKey,
        })),
      }));

      setOptions(transformedOptions);

      // Find and set the selected label if there's a selected value
      if (selectedValue) {
        for (const section of transformedOptions) {
          const item = section.items.find(
            (item) => item.value === selectedValue,
          );
          if (item) {
            setSelectedLabel(item.label);
            break;
          }
        }
      }
    } else if (!field.useTranslation) {
      // Use direct options if no translation
      const directOptions = field.options.map((section) => ({
        section: section.section_name,
        items: (section.options || []).map((option) => ({
          value: option.value,
          label: option.label,
        })),
      }));

      setOptions(directOptions);

      // Find and set the selected label if there's a selected value
      if (selectedValue) {
        for (const section of directOptions) {
          const item = section.items.find(
            (item) => item.value === selectedValue,
          );
          if (item) {
            setSelectedLabel(item.label);
            break;
          }
        }
      }
    }
  }, [translations, field, selectedValue]);

  return (
    <Autocomplete
      isClearable
      size="sm"
      labelPlacement="outside"
      key={field.name}
      label={field.label}
      isLoading={field.useTranslation && isLoading}
      selectedKey={selectedValue}
      inputValue={selectedLabel}
      onInputChange={(inputValue) => {
        if (!inputValue) {
          // Handle clear action: reset the field value
          setValue(field.name as Path<T>, "" as PathValue<T, Path<T>>);
          setSelectedLabel("");
          return;
        }

        // Only update the display input, actual value is set by onSelectionChange
        setSelectedLabel(inputValue);
      }}
      onSelectionChange={(key) => {
        if (key) {
          setValue(field.name as Path<T>, key as PathValue<T, Path<T>>);

          // Find the corresponding label
          for (const section of options) {
            const item = section.items.find((item) => item.value === key);
            if (item) {
              setSelectedLabel(item.label);
              break;
            }
          }
        } else {
          setValue(field.name as Path<T>, "" as PathValue<T, Path<T>>);
          setSelectedLabel("");
        }
      }}
      onClear={() => {
        setValue(field.name as Path<T>, "" as PathValue<T, Path<T>>);
        setSelectedLabel("");
      }}
      inputProps={{
        classNames: {
          inputWrapper:
            "bg-white hover:bg-white hover:border-slate-400 data-[hover=true]:bg-white group-data-[focus=true]:bg-white group-data-[focus=true]:border-primary border-2 shadow-sm",
          label: "text-blue-600 group-data-[filled-within=true]:text-blue-600",
        },
      }}
    >
      {options.map((section, index) => (
        <AutocompleteSection
          key={index}
          title={section.section}
          showDivider={index > 0}
        >
          {section.items.map((item) => (
            <AutocompleteItem key={item.value} textValue={item.label}>
              {item.label}
            </AutocompleteItem>
          ))}
        </AutocompleteSection>
      ))}
    </Autocomplete>
  );
}
